/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: CSV 上传解析 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-09 16:32:18
 */

import { useState, useCallback } from 'react'
import { message } from 'antd'

import {
  WatermarkExtractionStatus,
  WatermarkItem
} from '../../context/WatermarkBatchContext'

export interface TestDataItem {
  key: string
  newUrl: string // 手动
  oldUrl: string // 原图
  autoUrl?: string // 自动（应用后生成的图片）
  newBaseId: string
  oldBaseId: string
  usedItem: string
  newUsedItem: string
  diff: string
  newWmContentList: string
  oldWmContentList: string
  extractionStatus?: WatermarkExtractionStatus // 提取状态
  taskID?: string // 任务ID
  errorMsg?: string // 错误信息
  watermarkData?: WatermarkItem[] // 水印数据（提取完成后保存）
  watermarkID?: string // 水印ID
  applyStatus?: number // 应用状态（0-失败，1-成功，2-无修改项，3-超时）
  applyShowReason?: string // 应用错误原因
  applyDuration?: number // 应用处理时长（毫秒）
  applyTaskID?: string // 应用的 taskID
  remark?: string // 备注
  isProcessed?: boolean // 是否已处理标记
  processedBy?: string // 处理人昵称
}

interface UseCSVUploadReturn {
  dataSource: TestDataItem[]
  loading: boolean
  handleFileUpload: (file: File) => void
  updateDataSource: (updater: (prev: TestDataItem[]) => TestDataItem[]) => void
}

/**
 * CSV 解析函数（参考 papaparse 的逻辑实现）
 * 正确处理：
 * 1. 引号包裹的字段
 * 2. 转义引号（"" 表示一个引号字符）
 * 3. 引号内的换行符和逗号
 * 4. 各种 CSV 格式
 */
const parseCSV = (csvText: string): TestDataItem[] => {
  const expectedHeaders = [
    'newUrl',
    'oldUrl',
    'newBaseId',
    'oldBaseId',
    'usedItem',
    'newUsedItem',
    'diff',
    'newWmContentList',
    'oldWmContentList'
  ]

  const rows: string[][] = []
  const quoteChar = '"'
  const delimiter = ','
  let currentRow: string[] = []
  let currentField = ''
  let inQuotes = false
  let i = 0

  // 解析 CSV 文本
  while (i < csvText.length) {
    const char = csvText[i]
    const nextChar = csvText[i + 1]

    if (char === quoteChar) {
      if (inQuotes) {
        if (nextChar === quoteChar) {
          // 转义引号（"" 表示一个引号字符）
          currentField += quoteChar
          i += 2 // 跳过两个引号
          continue
        } else if (
          nextChar === delimiter ||
          nextChar === '\n' ||
          nextChar === '\r' ||
          i === csvText.length - 1
        ) {
          // 引号结束，字段结束
          inQuotes = false
          i++
          continue
        }
      } else {
        // 开始引号包裹
        inQuotes = true
        i++
        continue
      }
    }

    if (!inQuotes) {
      if (char === delimiter) {
        // 字段分隔符
        currentRow.push(currentField)
        currentField = ''
        i++
        continue
      } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
        // 行结束
        currentRow.push(currentField)
        if (currentRow.length > 0 && currentRow.some((field) => field.trim())) {
          // 跳过完全空白的行
          rows.push(currentRow)
        }
        currentRow = []
        currentField = ''
        if (char === '\r' && nextChar === '\n') {
          i += 2 // 跳过 \r\n
        } else {
          i++
        }
        continue
      }
    }

    // 添加到当前字段
    currentField += char
    i++
  }

  // 处理最后一行
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField)
    if (currentRow.length > 0 && currentRow.some((field) => field.trim())) {
      rows.push(currentRow)
    }
  }

  if (rows.length < 2) {
    throw new Error('CSV 文件至少需要包含表头和数据行')
  }

  // 检查表头
  const headers = rows[0]
  if (headers.length !== expectedHeaders.length) {
    throw new Error(
      `表头列数不匹配，期望 ${expectedHeaders.length} 列，实际 ${
        headers.length
      } 列。表头：${headers.join(', ')}`
    )
  }

  // 解析数据行
  const data: TestDataItem[] = []
  let skippedCount = 0

  for (let i = 1; i < rows.length; i++) {
    const values = rows[i]

    // 如果列数不匹配，跳过这一行
    if (values.length !== expectedHeaders.length) {
      skippedCount++
      if (skippedCount <= 10) {
        console.warn(
          `第 ${i + 1} 行列数不匹配，期望 ${expectedHeaders.length} 列，实际 ${
            values.length
          } 列`
        )
      }
      continue
    }

    const item: TestDataItem = {
      key: `${i}`,
      newUrl: values[0] || '', // 手动
      oldUrl: values[1] || '', // 原图
      autoUrl: '', // 自动（默认为空，应用后生成的图片会放到这里）
      newBaseId: values[2] || '',
      oldBaseId: values[3] || '',
      usedItem: values[4] || '',
      newUsedItem: values[5] || '',
      diff: values[6] || '',
      newWmContentList: values[7] || '',
      oldWmContentList: values[8] || ''
    }

    data.push(item)
  }

  if (skippedCount > 0) {
    console.warn(`共跳过 ${skippedCount} 行数据（列数不匹配）`)
    message.warning(`成功解析 ${data.length} 条数据，跳过 ${skippedCount} 行`)
  } else {
    console.log(`成功解析 ${data.length} 条数据`)
  }

  return data
}

export const useCSVUpload = (): UseCSVUploadReturn => {
  const [dataSource, setDataSource] = useState<TestDataItem[]>([])
  const [loading, setLoading] = useState(false)

  const handleFileUpload = useCallback((file: File) => {
    setLoading(true)
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string
        const parsedData = parseCSV(content)
        setDataSource(parsedData)
        message.success(`成功上传 ${parsedData.length} 条测试数据`)
        setLoading(false)
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : '文件解析失败，请检查文件格式'
        message.error(errorMessage)
        setLoading(false)
      }
    }

    reader.onerror = () => {
      message.error('文件读取失败')
      setLoading(false)
    }

    reader.readAsText(file, 'UTF-8')
  }, [])

  const updateDataSource = useCallback(
    (updater: (prev: TestDataItem[]) => TestDataItem[]) => {
      setDataSource(updater)
    },
    []
  )

  return {
    dataSource,
    loading,
    handleFileUpload,
    updateDataSource
  }
}
